import type { FC, ReactNode } from 'react'
import type { Locale } from '@/i18n/config'
import type { AuthorProfile, CaseStudyCard, ContentCard, ContentType } from '@/lib/content/types'

import Link from 'next/link'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cmsKey, localized } from '@/lib/portal/cms'
import { absoluteUrl } from '@/components/generals/seo'
import { localePath } from '@/i18n/config'
import { siteConfig } from '@/constants/site'

/** Database table behind each content type, for inline editing keys. */
export const contentTable = (type: ContentType): string => type === 'insight' ? 'insights' : type === 'case_study' ? 'case_studies' : 'posts'

/** Public section a content type lives under. */
export const contentSection = (type: ContentType): string => type === 'insight' ? '/insights' : type === 'case_study' ? '/case-studies' : '/blog'

export const contentHref = (type: ContentType, slug: string): string => `${contentSection(type)}/${slug}`

/** `localized` for typed rows: the value for the locale and the column it came from. */
export const field = (row: object, column: string, locale: Locale): { value: string, column: string } => localized(row as Record<string, unknown>, column, locale)

/** The summary of a card, tagged against `excerpt` for posts and `summary` otherwise. */
export const summaryOf = (item: ContentCard, locale: Locale): { value: string, column: string } => {
    const column = item.summary_column

    if (locale === 'sq' && item.summary__sq && item.summary__sq.trim() !== '') {
        return { value: item.summary__sq, column: `${column}__sq` }
    }

    return { value: item.summary ?? '', column }
}

/** The topic name of a card for the locale, or null when the card has no topic. */
export const topicOf = (item: ContentCard, locale: Locale): { id: string, value: string, column: string } | null => {
    if (!item.topic_id || !item.topic_name) return null
    const name = localized({ name: item.topic_name, name__sq: item.topic_name__sq }, 'name', locale)
    return { id: item.topic_id, ...name }
}

export const isCaseStudyCard = (item: ContentCard): item is CaseStudyCard => item.type === 'case_study' && 'client_label' in item

interface ArticleJsonLdInput {
    headline: string
    description?: string
    path: string
    locale: Locale
    datePublished: string | null
    dateModified: string | null
    image?: string | null
    author?: { name: string, jobTitle?: string | null } | null
}

/** schema.org Article for a detail page, published by Ura Advisory. */
export const articleJsonLd = ({ headline, description, path, locale, datePublished, dateModified, image, author }: ArticleJsonLdInput): Record<string, unknown> => {
    const url = absoluteUrl(localePath(locale, path))

    const publisher = {
        '@type': 'Organization',
        name: siteConfig.name,
        url: siteConfig.url,
        logo: { '@type': 'ImageObject', url: `${siteConfig.url}/api/brand/icon` }
    }

    return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: headline.slice(0, 110),
        ...(description ? { description } : {}),
        ...(datePublished ? { datePublished } : {}),
        ...(dateModified || datePublished ? { dateModified: dateModified ?? datePublished } : {}),
        inLanguage: locale === 'sq' ? 'sq' : 'en',
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        url,
        image: image ?? absoluteUrl(`/api/og?title=${encodeURIComponent(headline.slice(0, 140))}&locale=${locale}`),
        author: author
            ? { '@type': 'Person', name: author.name, ...(author.jobTitle ? { jobTitle: author.jobTitle } : {}), worksFor: { '@type': 'Organization', name: siteConfig.name } }
            : { '@type': 'Organization', name: siteConfig.name, url: siteConfig.url },
        publisher
    }
}

interface TopicChipProps {
    item: ContentCard
    locale: Locale
    tone?: 'light' | 'dark'
    className?: string
}

export const TopicChip: FC<TopicChipProps> = ({ item, locale, tone = 'light', className }): ReactNode => {
    const topic = topicOf(item, locale)
    if (!topic) return null

    return (
        <span
            className={cn(
                'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium',
                tone === 'dark' ? 'border-white/15 bg-white/5 text-white/85' : 'border-primary/15 bg-accent text-accent-foreground',
                className
            )}
            data-mdlfy-cms={cmsKey('topics', topic.id, topic.column)}
        >
            {topic.value}
        </span>
    )
}

interface BreadcrumbItem {
    href?: string
    label: ReactNode
}

interface BreadcrumbProps {
    items: Array<BreadcrumbItem>
    label: string
    tone?: 'light' | 'dark'
}

export const Breadcrumb: FC<BreadcrumbProps> = ({ items, label, tone = 'light' }): ReactNode => {
    const dark = tone === 'dark'

    return (
        <nav aria-label={label}>
            <ol className={cn('flex flex-wrap items-center gap-1.5 text-sm', dark ? 'text-white/60' : 'text-muted-foreground')}>
                {items.map((item, index) => {
                    const last = index === items.length - 1

                    return (
                        <li key={index} className={cn('flex min-w-0 items-center gap-1.5', last && 'max-w-full sm:max-w-md')}>
                            {item.href && !last ? (
                                <Link href={item.href} className={cn('transition-colors', dark ? 'hover:text-white' : 'hover:text-foreground')}>
                                    {item.label}
                                </Link>
                            ) : (
                                <span className={cn('line-clamp-1', dark ? 'text-white/85' : 'text-foreground/80')} aria-current={last ? 'page' : undefined}>
                                    {item.label}
                                </span>
                            )}

                            {!last && <ChevronRight className='size-4 shrink-0 opacity-60' aria-hidden='true' />}
                        </li>
                    )
                })}
            </ol>
        </nav>
    )
}

const initialsOf = (name: string): string => name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('')

interface AuthorAvatarProps {
    author: AuthorProfile
    className?: string
    tone?: 'light' | 'dark'
}

export const AuthorAvatar: FC<AuthorAvatarProps> = ({ author, className, tone = 'light' }): ReactNode => {
    if (author.avatar) {
        return (
            // eslint-disable-next-line @next/next/no-img-element
            <img
                src={author.avatar}
                alt={author.avatar_alt ?? ''}
                className={cn('size-11 shrink-0 rounded-full object-cover', className)}
                loading='lazy'
                decoding='async'
            />
        )
    }

    return (
        <span
            className={cn(
                'flex size-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold tracking-wide',
                tone === 'dark' ? 'bg-white/10 text-white' : 'bg-ink text-white',
                className
            )}
            aria-hidden='true'
        >
            {initialsOf(author.name)}
        </span>
    )
}
